// Component: PricingPlans
// Purpose: Display pricing plan cards with features and selection functionality

import React from 'react';
import { Check } from 'lucide-react';
import { ButtonSpinner } from '../../ui/feedback/loading-spinner.jsx';
import { ORIGINAL_PRICING, PLAN_DISCOUNTS } from '../../../data/pricing/pricingPlans';

// Helper function to define CSS classes
function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export function PricingPlans({ 
  pricing, 
  frequency, 
  isLoading, 
  selectedPlan, 
  handleSelectPlan, 
  isFromSettings, 
  isFromSignup, 
  isFromCreateTemplate, 
  userPlan, 
  hasHadFreeTrial, 
  isLoggedIn, 
  children 
}) {
  return (
    <div className="relative mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-2">
      {pricing.tiers.map((tier) => {
        const isSelected = selectedPlan === tier.id;
        const isUserCurrentPlan = userPlan === tier.planId;
        const isEnterprise = tier.isEnterprise;
        
        // Show "Current Plan" for user's active plan
        let buttonText = hasHadFreeTrial ? 'Get Started' : 'Start Free Trial';
        if (isEnterprise) {
          buttonText = tier.contactInfo?.buttonText || 'Contact Sales';
        } else if (isFromSettings && isUserCurrentPlan) {
          buttonText = 'Current Plan';
        } else if (isFromSettings) {
          buttonText = 'Upgrade';
        }
        
        return (
        <div
          key={tier.id}
          className={classNames(
            tier.featured
              ? 'z-10 bg-gradient-to-b from-indigo-500 to-purple-600 shadow-xl dark:from-indigo-600 dark:to-purple-700 ' + (tier.featured ? 'scale-105' : '')
              : 'bg-white dark:bg-gray-800/80',
            'relative rounded-2xl ring-1 ring-gray-200 dark:ring-gray-700 backdrop-blur-sm overflow-hidden',
            isSelected ? 'ring-2 ring-purple-600' : ''
          )}
        >

          
                              <div className="p-8 lg:pt-12 xl:p-10 xl:pt-14">
            <h2
              id={tier.id}
              className={classNames(
                tier.featured ? 'text-white' : 'text-gray-900 dark:text-white',
                'text-2xl font-bold'
              )}
            >
              {tier.name}
            </h2>
            <p className={classNames(
              tier.featured ? 'text-indigo-100' : 'text-gray-600 dark:text-gray-300',
              'mt-2 text-sm'
            )}>
              {tier.description}
            </p>
            
            <div className="mt-6">
              {/* Show original pricing crossed out for Premium with discount badge */}
              {tier.name === 'Premium' && ORIGINAL_PRICING.premium && (
                <div className="mb-2 flex items-center gap-2">
                  <span className={classNames(
                    tier.featured ? 'text-indigo-100/60' : 'text-gray-500 dark:text-gray-400',
                    'text-lg line-through'
                  )}>
                    {ORIGINAL_PRICING.premium[frequency.value]}
                  </span>
                  <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                    {PLAN_DISCOUNTS.premium?.label || '30% off'}
                  </span>
                </div>
              )}
              
              {/* Show discount badge for Enterprise without crossed-out pricing */}
              {tier.name === 'Enterprise' && PLAN_DISCOUNTS.enterprise && (
                <div className="mb-2 flex items-center gap-2">
                  <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                    {PLAN_DISCOUNTS.enterprise?.label || '30% off'}
                  </span>
                </div>
              )}
              
              <p className={classNames(
                  tier.featured ? 'text-white' : 'text-gray-900 dark:text-white',
                  isEnterprise ? 'text-3xl' : 'text-5xl',
                  'font-bold tracking-tight flex items-baseline'
                )}
              >
                <span>{tier.price[frequency.value]}</span>
                <span className="text-sm font-normal ml-1 opacity-60">/mo</span>
              </p>
              <p className={classNames(
                tier.featured ? 'text-indigo-100' : 'text-gray-500 dark:text-gray-400',
                'mt-1 text-sm'
              )}>
                {isEnterprise 
                  ? 'Discounted pricing for more customers' 
                  : frequency.value === 'annual' 
                    ? 'Billed annually (30% off)' 
                    : 'Billed monthly'
                }
              </p>
              {!hasHadFreeTrial && !isFromSettings && !isEnterprise && (
                <p className={classNames(
                  tier.featured ? 'text-indigo-100/90' : 'text-purple-600 dark:text-purple-400',
                  'mt-2 text-sm font-medium'
                )}>
                  Includes 3-day free trial
                </p>
              )}
            </div>
            
            {/* Button */}
            <div className="mt-8">
              {isEnterprise ? (
                <a
                  href={tier.contactInfo?.calendlyUrl || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classNames(
                    tier.featured
                      ? 'bg-white text-purple-600 hover:bg-gray-50'
                      : 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700',
                    'block w-full rounded-lg px-3 py-3 text-center text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 transition-all duration-200 no-underline'
                  )}
                >
                  {buttonText}
                </a>
              ) : (
                <button
                  onClick={() => handleSelectPlan(tier.id, tier.planId)}
                  disabled={isLoading || (isFromSettings && isUserCurrentPlan)}
                  className={classNames(
                    tier.featured
                      ? 'bg-white text-purple-600 hover:bg-gray-50 disabled:hover:bg-white'
                      : 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 disabled:from-purple-600 disabled:to-indigo-600',
                    'block w-full rounded-lg px-3 py-3 text-center text-sm font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200'
                  )}
                >
                  {isLoading && isSelected ? (
                    <div className="flex justify-center items-center">
                      <ButtonSpinner color="current" />
                      Processing...
                    </div>
                  ) : isFromSettings ? (
                    isUserCurrentPlan ? 'Current Plan' : 'Manage Subscription'
                  ) : (
                    buttonText
                  )}
                </button>
              )}
            </div>
            
            {/* Features List */}
            <div className="mt-8 space-y-4">
              {tier.highlights.map((feature) => (
                <div key={feature} className="flex gap-x-3">
                  <div className={classNames(
                    tier.featured 
                      ? 'bg-white/20 text-white' 
                      : 'bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-400',
                    'flex h-6 w-6 shrink-0 items-center justify-center rounded-full'
                  )}>
                    <Check className="h-4 w-4" />
                  </div>
                  <p className={classNames(
                    tier.featured ? 'text-white' : 'text-gray-700 dark:text-gray-300',
                    'text-sm'
                  )}>
                    {feature}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
        );
      })}
      {children}
    </div>
  );
}

// Simple informational card highlighting RoboCent subscription options
export function RoboCentSubscriptionsCard({ isAnnual = false }) {
  const brandOrange = '#F05A28';
  const displayPrice = isAnnual ? '$80' : '$97';
  const billingText = isAnnual ? 'Billed annually (Jumpstart)' : 'Billed monthly (Jumpstart)';
  return (
    <div className="w-full h-full">
      <div
        className="relative rounded-3xl ring-1 ring-gray-200 dark:ring-gray-700 backdrop-blur-sm overflow-hidden bg-white dark:bg-gray-800 shadow-md h-full flex flex-col"
        style={{ borderColor: 'rgba(240,90,40,0.35)' }}
      >
        <div className="p-8 lg:pt-12 xl:p-10 xl:pt-14 flex-1 flex flex-col">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: 'rgba(240,90,40,0.12)' }}>
              <img src="/images/logos/robocent.png" alt="RoboCent" className="h-4 w-4 object-contain" />
            </div>
            <h2 className="text-gray-900 dark:text-white text-2xl font-bold">RoboCent Subscriptions</h2>
          </div>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            Choose an outreach subscription that includes Advocacy Lab plus texting, voice, voter data, and more.
          </p>
          {/* Price block without a surrounding box */}
          <div className="mt-6">
            <span className="inline-block mb-2 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: 'rgba(240,90,40,0.12)', color: brandOrange }}>
              Subscriptions starting at
            </span>
            <div className="flex items-baseline gap-2">
              <p className="text-5xl font-bold text-gray-900 dark:text-white">{displayPrice}</p>
              <span className="text-sm text-gray-500 dark:text-gray-400">/mo</span>
            </div>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">{billingText}</p>
          </div>
          <a
            href="https://docs.robocent.com/explore/pricing/subscription-packages"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex w-full items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold shadow-sm transition-colors bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            style={{ color: brandOrange, border: `1px solid ${brandOrange}55` }}
          >
            View Subscription Options
          </a>
          <ul className="mt-6 space-y-3 text-sm text-gray-700 dark:text-gray-300">
            {[
              'Text Messaging — SMS/MMS with optional managed sending and 14-day two-way messaging',
              'Voice — robocalls, live transfers, and keypad surveys (with voicemail)',
              'Voter Data — landline, mobile, email, and carrier lookups with appends',
              'Web & Hosting — 10DLC, website hosting, and support; fees often waived on higher tiers',
              'Strategy & Support — expedited onboarding, priority support, and regular audits',
              'Creative — instant campaign graphics and professional voice talent',
            ].map((text) => (
              <li key={text} className="flex items-start gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4 mt-0.5" style={{ color: brandOrange }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
